// ROI Command Center - Iteration 7
// Based on V6 design from Figma
// Enterprise-grade, CFO-ready ROI modeling workspace

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, Copy, FileDown, AlertCircle,
  TrendingUp, TrendingDown, RefreshCw, HelpCircle, Activity, CheckCircle2,
  AlertTriangle, Edit2, Sparkles, ArrowUpRight, Info, Target,
  DollarSign, Settings, Users, Database, TrendingUpIcon, Lightbulb,
  BarChart3, Compass, Loader2,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
  ReferenceLine,
} from 'recharts';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import type { ROIScenario, AssumptionGroup, ScenarioId } from '../../data/roiMockData';
import type { ROIModelResponse } from '../../services/roiService';
import {
  buildAssumptionGroupsFromAssumptionLab,
  buildWaterfallBarsFromFinancial,
  buildSensitivityRankingFromLab,
  collectCustomAssumptionOverrides,
  narrativeFromScenarioDetails,
  detailsForScenario,
  buildCustomScenarioNarrative,
  formatAssumptionDisplayValue,
  pickFinancialBreakdownForScenario,
  resolveCustomScenario,
} from '../../services/roiService';

// ─── Static per-scenario narrative content ───────────────────────────────────
const SCENARIO_STATIC: Record<string, Omit<ROIScenario, 'id' | 'roi' | 'paybackPeriod' | 'netValue3Year' | 'confidence'>> = {
  conservative: {
    name: 'Conservative',
    sublabel: 'Standard Model',
    description: 'Lower bound estimate with cautious assumptions and extended timelines',
    valueDrivers: ['Conservative adoption targets applied', 'Extended implementation timeline', 'Reduced patient population scope'],
    keyAssumptions: ['65% user adoption floor', 'Conservative efficiency gains', 'Longer deployment cycle'],
    risks: ['Change management resistance may slow adoption', 'Legacy system integration complexity'],
  },
  base: {
    name: 'Base Case',
    sublabel: 'Standard Model',
    description: 'Most likely outcome based on industry benchmarks and comparable implementations',
    valueDrivers: ['Optimized patient recruitment pipeline', 'Data-driven site selection', 'Reduced screen failure rates'],
    keyAssumptions: ['65% user adoption', 'Standard 6-month deployment', '22% process efficiency gain'],
    risks: ['Vendor delivery timeline dependencies', 'Resource availability constraints'],
  },
  aggressive: {
    name: 'Aggressive',
    sublabel: 'Standard Model',
    description: 'Optimistic scenario with accelerated benefits realization and strategic wins',
    valueDrivers: ['Accelerated adoption cycle', 'Maximum efficiency gains realized', 'Expanded patient population reach'],
    keyAssumptions: ['87.75% adoption target', 'Accelerated 5.3-month deployment', '29.7% efficiency gain'],
    risks: ['Requires sustained executive sponsorship', 'Ambitious timeline may stress resources'],
  },
  custom: {
    name: 'Custom',
    sublabel: 'User Modified',
    description: 'User-defined scenario with custom assumption overrides',
    valueDrivers: ['Based on your custom assumption inputs'],
    keyAssumptions: ['Custom inputs applied in Assumptions Lab below'],
    risks: ['Custom scenario requires benchmark validation'],
  },
};

// Icon mapping for assumption groups
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  DollarSign,
  Settings,
  Users,
  Target,
};

interface ROICommandCenterProps {
  useCaseName?: string;
  roiData?: ROIModelResponse | null;
  /** When true, shows a loader instead of placeholder/mock content (API in flight). */
  isLoading?: boolean;
  activeScenario?: ScenarioId;
  onScenarioChange?: (scenario: ScenarioId) => void;
  onExportOnePager?: () => void;
  onExportBoardDeck?: () => void;
}

export default function ROICommandCenter({
  useCaseName = 'Use Case',
  roiData,
  isLoading = false,
  activeScenario = 'base',
  onScenarioChange,
  onExportOnePager,
  onExportBoardDeck,
}: ROICommandCenterProps) {
  const navigate = useNavigate();

  // ─── Assumption groups state (initialized from API, editable) ────────────
  const [assumptionGroups, setAssumptionGroups] = useState<AssumptionGroup[]>(() =>
    roiData?.data.assumptionLab ? buildAssumptionGroupsFromAssumptionLab(roiData.data.assumptionLab) : []
  );

  useEffect(() => {
    if (roiData?.data.assumptionLab) {
      setAssumptionGroups(buildAssumptionGroupsFromAssumptionLab(roiData.data.assumptionLab));
    } else {
      setAssumptionGroups([]);
    }
  }, [roiData]);

  // Custom column values drive the Custom card so edits in the Assumptions Laboratory
  // recalculate instead of echoing Base (DMD-1106).
  const customOverrides = useMemo(
    () => collectCustomAssumptionOverrides(assumptionGroups),
    [assumptionGroups],
  );

  const customScenario = useMemo(
    () => resolveCustomScenario(roiData?.data, customOverrides),
    [roiData, customOverrides],
  );

  // ─── Derive scenario cards from API data ─────────────────────────────────
  const derivedScenarios = useMemo<ROIScenario[]>(() => {
    if (!roiData) return [];
    const { scenarioCards } = roiData.data;

    const apiScenarios: ROIScenario[] = (
      ['conservative', 'base', 'aggressive'] as const
    ).map(id => {
      const card = scenarioCards[id];
      const meta = SCENARIO_STATIC[id];
      const narrative = narrativeFromScenarioDetails(
        detailsForScenario(roiData.data, id),
        meta,
      );
      return {
        id,
        ...meta,
        ...narrative,
        roi: card.roi / 1_000_000,          // store in millions for formatCurrency
        paybackPeriod: card.paybackMonths,
        netValue3Year: card.net3YearValue / 1_000_000,
        confidence: card.confidence,
      };
    });

    const customCard = customScenario?.card ?? scenarioCards.base;
    const customMeta = SCENARIO_STATIC.custom;
    // DMD-1182: Custom detail must match Custom summary card metrics/drivers.
    const customNarrative = buildCustomScenarioNarrative(
      roiData.data,
      customOverrides,
      customCard,
      customMeta,
    );
    apiScenarios.push({
      id: 'custom',
      ...customMeta,
      ...customNarrative,
      roi: customCard.roi / 1_000_000,
      paybackPeriod: customCard.paybackMonths,
      netValue3Year: customCard.net3YearValue / 1_000_000,
      confidence: customCard.confidence,
      modifiedCount: customScenario?.modifiedCount ?? 0,
    });

    return apiScenarios;
  }, [roiData, customScenario, customOverrides]);

  // ─── Waterfall chart data from API ───────────────────────────────────────
  const activeWaterfallData = useMemo(
    () =>
      buildWaterfallBarsFromFinancial(
        activeScenario === 'custom' && customScenario
          ? customScenario.financialBreakdown
          : pickFinancialBreakdownForScenario(roiData?.data, activeScenario),
      ),
    [roiData, activeScenario, customScenario],
  );

  // ─── Sensitivity: ±20% Δ Net Value per assumption (DMD-1184) ─────────────
  const activeSensitivityData = useMemo(() => {
    if (!roiData?.data.assumptionLab || Object.keys(roiData.data.assumptionLab).length === 0) {
      return [];
    }
    return buildSensitivityRankingFromLab(roiData.data, customOverrides);
  }, [roiData, customOverrides]);

  // ─── AI insight text ──────────────────────────────────────────────────────
  const aiInsightText = roiData?.data.aiInsights ?? null;

  const [financialNarrative, setFinancialNarrative] = useState<string>('');
  const [strategicNarrative, setStrategicNarrative] = useState<string>('');
  const [copiedText, setCopiedText] = useState<string | null>(null);

  useEffect(() => {
    const insight = roiData?.data?.aiInsights?.trim();
    if (insight) setFinancialNarrative(insight);
    else if (!roiData) setFinancialNarrative('');
  }, [roiData?.data?.aiInsights, roiData]);

  if (isLoading) {
    return (
      <div className="min-h-[420px] flex flex-col items-center justify-center gap-4 rounded-xl border border-[#e4e4e7] bg-white px-6 py-16">
        <Loader2 className="h-10 w-10 animate-spin text-[#18365E]" aria-hidden />
        <p className="text-sm font-medium text-[#52525b]">Loading ROI model…</p>
        <p className="text-xs text-[#71717a] text-center max-w-sm">
          Pulling scenarios, assumptions, and financial breakdown from the server.
        </p>
      </div>
    );
  }

  if (!roiData) {
    return (
      <div className="min-h-[200px] flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#e4e4e7] bg-[#fafafa] px-6 py-12">
        <p className="text-sm font-medium text-[#52525b]">No ROI model data</p>
        <p className="text-xs text-[#71717a]">Open a use case from the portfolio to load the model.</p>
      </div>
    );
  }

  const selectedScenario =
    derivedScenarios.find(s => s.id === activeScenario) ??
    derivedScenarios.find(s => s.id === 'base') ??
    derivedScenarios[0];
  const baseScenario = derivedScenarios.find(s => s.id === 'base') ?? derivedScenarios[0];

  const formatCurrency = (millions: number): string => `$${millions.toFixed(1)}M`;

  const getDeltaFromBase = (value: number, baseValue: number) => {
    const delta = value - baseValue;
    return { delta: Math.abs(delta), isUp: delta > 0, percent: baseValue !== 0 ? Math.abs((delta / baseValue) * 100) : 0 };
  };

  const handleAssumptionEdit = (assumptionId: string, newValue: number) => {
    setAssumptionGroups(prevGroups =>
      prevGroups.map(group => ({
        ...group,
        assumptions: group.assumptions.map(assumption =>
          assumption.id === assumptionId
            ? {
                ...assumption,
                custom: newValue,
                changed: (() => {
                  const baseline = assumption.baseRaw ?? assumption.base;
                  return Math.abs(newValue - baseline) > 1e-9 * Math.max(1, Math.abs(baseline));
                })(),
              }
            : assumption
        )
      }))
    );
  };

  const handleResetAssumption = (assumptionId: string) => {
    setAssumptionGroups(prevGroups =>
      prevGroups.map(group => ({
        ...group,
        assumptions: group.assumptions.map(assumption =>
          assumption.id === assumptionId
            ? {
                ...assumption,
                custom: assumption.baseRaw ?? assumption.base,
                changed: false,
              }
            : assumption
        )
      }))
    );
  };

  const toggleGroupExpanded = (groupId: string) => {
    setAssumptionGroups(prevGroups =>
      prevGroups.map(group =>
        group.id === groupId
          ? { ...group, expanded: !group.expanded }
          : group
      )
    );
  };

  const handleCopy = async (text: string, type: 'financial' | 'strategic') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(type);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'External Benchmark':
        return <Database className="w-3.5 h-3.5 text-[#3b82f6]" />;
      case 'Historical':
        return <TrendingUpIcon className="w-3.5 h-3.5 text-[#10b981]" />;
      case 'Estimate':
        return <Lightbulb className="w-3.5 h-3.5 text-[#f59e0b]" />;
      default:
        return <Info className="w-3.5 h-3.5 text-[#71717a]" />;
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <div className="max-w-[1600px] mx-auto px-4 py-5">

        {/* Scenario Performance Overview */}
        <div className="mb-6">
          <div className="flex items-center gap-1.5 mb-3">
            <h3 className="text-[11px] text-[#52525b] uppercase tracking-wider" style={{ fontWeight: 700 }}>
              Scenario Performance Overview
            </h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-3.5 h-3.5 text-[#71717a] hover:text-[#18365E] cursor-pointer" />
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-[#18365E] text-white border-0 max-w-sm">
                  <p className="text-[10px]" style={{ fontWeight: 500 }}>
                    Select a scenario to view detailed breakdown. Click any card to switch active scenario.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Scenario Cards */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            {derivedScenarios.map((scenario) => {
              const isSelected = scenario.id === activeScenario;
              const roiDelta = getDeltaFromBase(scenario.roi, baseScenario.roi);
              const IconComponent = iconMap[scenario.id === 'custom' ? 'Settings' : 'DollarSign'] || DollarSign;

              return (
                <TooltipProvider key={scenario.id}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => onScenarioChange?.(scenario.id)}
                        className={`p-3 rounded-lg transition-all duration-200 text-left relative cursor-pointer ${isSelected
                            ? 'border-[3px] border-[#18365E] bg-[#18365E]/5 shadow-[0_0_12px_rgba(24,54,94,0.15),inset_0_0_0_1px_rgba(24,54,94,0.1)]'
                            : 'border-2 border-[#d4d4d8] bg-white hover:border-[#18365E]/40 hover:shadow-lg hover:-translate-y-0.5 hover:scale-[1.01]'
                          }`}
                      >
                        {/* Active Tag */}
                        {isSelected && (
                          <Badge className="absolute top-2 right-2 text-[8px] px-1.5 py-0 bg-[#18365E] text-white">
                            Active
                          </Badge>
                        )}

                        {/* Header */}
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span
                              className={`text-xs block mb-0.5 ${isSelected ? 'text-[#18365E]' : 'text-[#18365E]'}`}
                              style={{ fontWeight: 700 }}
                            >
                              {scenario.name}
                            </span>
                            <span className={`text-[9px] ${scenario.sublabel === 'User Modified'
                                ? 'text-[#FF6E4C]'
                                : 'text-[#71717a]'
                              }`} style={{ fontWeight: 500 }}>
                              {scenario.sublabel}
                            </span>
                          </div>
                          {scenario.modifiedCount && scenario.modifiedCount > 0 && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge className="text-[9px] px-1.5 py-0 bg-[#FF6E4C] text-white shadow-[0_0_8px_rgba(255,110,76,0.3)]">
                                    {scenario.modifiedCount} Modified
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent className="bg-[#18365E] text-white border-0">
                                  <p className="text-[10px]">Contains modified assumptions</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>

                        {/* Metrics */}
                        <div className="space-y-1 mb-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[9px] text-[#71717a]" style={{ fontWeight: 600 }}>Annual Value</span>
                            <div className="flex items-center gap-1">
                              <span className="text-base text-[#18365E]" style={{ fontWeight: 700 }}>
                                {formatCurrency(scenario.roi)}
                              </span>
                              {scenario.id !== 'base' && roiDelta.delta > 0 && (
                                <span className={`text-[9px] flex items-center ${roiDelta.isUp ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
                                  {roiDelta.isUp ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[9px] text-[#71717a]" style={{ fontWeight: 600 }}>Payback</span>
                            <span className="text-xs text-[#18365E]" style={{ fontWeight: 600 }}>
                              {scenario.paybackPeriod} mo
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[9px] text-[#71717a]" style={{ fontWeight: 600 }}>3-Yr Value</span>
                            <span className="text-xs text-[#18365E]" style={{ fontWeight: 600 }}>
                              {formatCurrency(scenario.netValue3Year)}
                            </span>
                          </div>
                        </div>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-[#18365E] text-white border-0 max-w-sm">
                      <p className="text-[10px] mb-1" style={{ fontWeight: 600 }}>
                        What differentiates this scenario
                      </p>
                      <p className="text-[9px] text-slate-300">
                        {scenario.description}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>

          {/* Scenario Detail Panel */}
          <div className="bg-gradient-to-br from-white to-[#fafafa] border border-[#d4d4d8] rounded-lg p-3">
            <div className="mb-2.5">
              <h4 className="text-sm text-[#18365E] mb-0.5 flex items-center gap-1.5" style={{ fontWeight: 700 }}>
                <Activity className="w-4 h-4" />
                Scenario: {selectedScenario.name}
              </h4>
              <p className="text-[10px] text-[#52525b]" style={{ fontWeight: 500 }}>
                {selectedScenario.description}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3 text-[10px]">
              {/* Value Drivers */}
              <div>
                <h5 className="text-[9px] text-[#52525b] mb-1.5 uppercase tracking-wider flex items-center gap-1 pb-1.5 border-b border-[#e4e4e7]" style={{ fontWeight: 700 }}>
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#10b981]" />
                  Value Drivers
                </h5>
                <ul className="space-y-1 mt-1.5">
                  {selectedScenario.valueDrivers.map((driver, index) => (
                    <li key={index} className="flex items-start gap-1.5">
                      <span className="text-[#10b981] text-xs flex-shrink-0 mt-0.5">✓</span>
                      <span className="text-[#27272a]" style={{ fontWeight: 500 }}>{driver}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Key Assumptions */}
              <div className="border-l border-[#e4e4e7] pl-3">
                <h5 className="text-[9px] text-[#52525b] mb-1.5 uppercase tracking-wider flex items-center gap-1 pb-1.5 border-b border-[#e4e4e7]" style={{ fontWeight: 700 }}>
                  <Target className="w-3.5 h-3.5 text-[#18365E]" />
                  Key Assumptions
                </h5>
                <ul className="space-y-1 mt-1.5">
                  {selectedScenario.keyAssumptions.map((assumption, index) => (
                    <li key={index} className="flex items-start gap-1.5">
                      <span className="text-[#3b82f6] text-xs flex-shrink-0 mt-0.5">•</span>
                      <span className="text-[#27272a]" style={{ fontWeight: 500 }}>{assumption}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Risks */}
              <div className="border-l border-[#e4e4e7] pl-3">
                <h5 className="text-[9px] text-[#52525b] mb-1.5 uppercase tracking-wider flex items-center gap-1 pb-1.5 border-b border-[#e4e4e7]" style={{ fontWeight: 700 }}>
                  <AlertCircle className="w-3.5 h-3.5 text-[#f59e0b]" />
                  Risks
                </h5>
                <ul className="space-y-1 mt-1.5">
                  {selectedScenario.risks.map((risk, index) => (
                    <li key={index} className="flex items-start gap-1.5">
                      <AlertCircle className="w-2.5 h-2.5 text-[#f59e0b] flex-shrink-0 mt-1" />
                      <span className="text-[#27272a]" style={{ fontWeight: 500 }}>{risk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Main 2-Column Core */}
        <div className="grid grid-cols-3 gap-6">
          {/* LEFT: Assumptions Lab + Waterfall */}
          <div className="col-span-2 space-y-6">
            {/* Assumptions Laboratory */}
            <div className="bg-white border border-[#d4d4d8] rounded-lg">
              <div className="px-3 py-2.5 border-b border-[#e4e4e7] bg-[#fafafa]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-xs text-[#18365E]" style={{ fontWeight: 700 }}>
                      Assumptions Laboratory
                    </h3>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="w-3.5 h-3.5 text-[#71717a] hover:text-[#18365E] cursor-pointer" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="bg-[#18365E] text-white border-0 max-w-md">
                          <p className="text-[10px] mb-1" style={{ fontWeight: 500 }}>
                            Edit assumptions to simulate scenarios. Changes apply to Custom scenario.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-[9px] text-[#52525b]" style={{ fontWeight: 500 }}>
                    Edit key drivers
                  </p>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-[10px]">
                  <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
                    <tr>
                      <th className="px-3 py-2 text-left text-[9px] text-[#52525b] uppercase tracking-wider" style={{ fontWeight: 700 }}>
                        Assumption
                      </th>
                      <th className="px-2 py-2 text-right text-[9px] text-[#52525b] uppercase tracking-wider w-16" style={{ fontWeight: 700 }}>
                        Cons.
                      </th>
                      <th className="px-2 py-2 text-right text-[9px] text-[#52525b] uppercase tracking-wider w-16" style={{ fontWeight: 700 }}>
                        Base
                      </th>
                      <th className="px-2 py-2 text-right text-[9px] text-[#52525b] uppercase tracking-wider w-16" style={{ fontWeight: 700 }}>
                        Agg.
                      </th>
                      <th className="px-2 py-2 text-right text-[9px] text-[#52525b] uppercase tracking-wider w-20" style={{ fontWeight: 700 }}>
                        Custom
                      </th>
                      <th className="px-2 py-2 text-center text-[9px] text-[#52525b] uppercase tracking-wider w-14" style={{ fontWeight: 700 }}>
                        Conf.
                      </th>
                      <th className="px-2 py-2 text-left text-[9px] text-[#52525b] uppercase tracking-wider w-28" style={{ fontWeight: 700 }}>
                        Source
                      </th>
                      <th className="px-2 py-2 text-center text-[9px] text-[#52525b] uppercase tracking-wider w-20" style={{ fontWeight: 700 }}>
                        Impact
                      </th>
                      <th className="px-2 py-2 w-6"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {assumptionGroups.map((group) => {
                      const GroupIcon = iconMap[group.icon] || DollarSign;
                      return (
                        <React.Fragment key={group.id}>
                          {/* Group Header */}
                          <tr
                            className="bg-[#18365E]/8 cursor-pointer hover:bg-[#18365E]/12 transition-colors"
                            onClick={() => toggleGroupExpanded(group.id)}
                          >
                            <td colSpan={9} className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <GroupIcon className="w-5 h-5 text-[#18365E]" />
                                {group.expanded ? (
                                  <ChevronDown className="w-3.5 h-3.5 text-[#18365E]" />
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5 text-[#18365E]" />
                                )}
                                <span className="text-[10px] text-[#18365E]" style={{ fontWeight: 700 }}>
                                  {group.name}
                                </span>
                                <span className="text-[9px] text-[#71717a]" style={{ fontWeight: 500 }}>
                                  ({group.assumptions.length})
                                </span>
                              </div>
                            </td>
                          </tr>
                          {/* Rows */}
                          {group.expanded && group.assumptions.map((assumption, rowIndex) => {
                            const isEvenRow = rowIndex % 2 === 0;
                            return (
                              <tr
                                key={assumption.id}
                                className={`border-b border-[#e4e4e7] hover:bg-[#18365E]/5 transition-colors ${isEvenRow ? 'bg-[#fafafa]/50' : 'bg-white'
                                  } ${assumption.changed ? 'bg-[#10b981]/5' : ''}`}
                                style={{ height: '46px' }}
                              >
                                <td className="px-3 py-2.5">
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex items-center gap-1.5">
                                          <Info className="w-3.5 h-3.5 text-[#71717a] cursor-help" />
                                          <span className="text-[#27272a]" style={{ fontWeight: 500 }}>
                                            {assumption.name}
                                          </span>
                                          {assumption.changed && (
                                            <div className="w-1.5 h-1.5 rounded-full bg-[#FF6E4C]" />
                                          )}
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent side="right" className="bg-[#18365E] text-white border-0 max-w-md">
                                        <div className="space-y-1.5">
                                          <p className="text-[10px] font-semibold">How this affects ROI</p>
                                          {assumption.description && (
                                            <p className="text-[9px] text-slate-300">{assumption.description}</p>
                                          )}
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </td>
                                <td className="px-2 py-2.5 text-right text-[#71717a]" style={{ fontWeight: 500 }}>
                                  {formatAssumptionDisplayValue(assumption.conservative)}{assumption.unit}
                                </td>
                                <td className="px-2 py-2.5 text-right text-[#18365E]" style={{ fontWeight: 600 }}>
                                  {formatAssumptionDisplayValue(assumption.base)}{assumption.unit}
                                </td>
                                <td className="px-2 py-2.5 text-right text-[#71717a]" style={{ fontWeight: 500 }}>
                                  {formatAssumptionDisplayValue(assumption.aggressive)}{assumption.unit}
                                </td>
                                <td className="px-2 py-2.5">
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={formatAssumptionDisplayValue(assumption.custom)}
                                    onChange={(e) => handleAssumptionEdit(assumption.id, parseFloat(e.target.value) || 0)}
                                    className={`w-full px-2 py-1 text-right bg-white border rounded text-[#18365E] transition-all ${assumption.changed
                                        ? 'border-[#FF6E4C] bg-[#FF6E4C]/5 ring-1 ring-[#FF6E4C]/20'
                                        : 'border-[#e4e4e7] hover:border-[#3b82f6] focus:border-[#3b82f6] focus:ring-1 focus:ring-[#3b82f6]/20'
                                      } focus:outline-none`}
                                    style={{ fontWeight: 600 }}
                                  />
                                </td>
                                <td className="px-2 py-2.5 text-center">
                                  <Badge className={`text-[9px] px-1 py-0 ${assumption.confidence === 'High' ? 'bg-[#10b981]/20 text-[#10b981]' :
                                      assumption.confidence === 'Medium' ? 'bg-[#f59e0b]/20 text-[#f59e0b]' :
                                        'bg-[#ef4444]/20 text-[#ef4444]'
                                    }`}>
                                    {assumption.confidence}
                                  </Badge>
                                </td>
                                <td className="px-2 py-2.5">
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex items-center gap-1 cursor-help">
                                          {getSourceIcon(assumption.sourceType)}
                                          <span className="text-[9px] text-[#52525b]" style={{ fontWeight: 500 }}>
                                            {assumption.source}
                                          </span>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="bg-[#18365E] text-white border-0">
                                        <p className="text-[10px]" style={{ fontWeight: 500 }}>
                                          <strong>{assumption.sourceType}</strong>
                                          <br />{assumption.source}
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </td>
                                <td className="px-2 py-2.5">
                                  <div className="flex items-center gap-1">
                                    <div className="flex-1 h-1.5 bg-[#e4e4e7] rounded-full overflow-hidden">
                                      <div
                                        className={`h-full transition-all ${assumption.impact >= 80 ? 'bg-[#10b981]' :
                                            assumption.impact >= 60 ? 'bg-[#f59e0b]' :
                                              'bg-[#71717a]'
                                          }`}
                                        style={{ width: `${assumption.impact}%` }}
                                      />
                                    </div>
                                    <span className="text-[9px] text-[#71717a] w-6 text-right" style={{ fontWeight: 500 }}>
                                      {assumption.impact}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-2 py-2.5 text-center">
                                  {assumption.changed && (
                                    <button
                                      onClick={() => handleResetAssumption(assumption.id)}
                                      className="text-[#71717a] hover:text-[#FF6E4C] transition-colors"
                                    >
                                      <RefreshCw className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Financial Breakdown Chart */}
            <div className="bg-white border border-[#d4d4d8] rounded-lg p-3.5">
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-xs text-[#18365E]" style={{ fontWeight: 700 }}>
                    Financial Breakdown (Year 1 Impact)
                  </h3>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3.5 h-3.5 text-[#71717a] hover:text-[#18365E] cursor-pointer" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="bg-[#18365E] text-white border-0 max-w-sm">
                        <p className="text-[10px]" style={{ fontWeight: 500 }}>
                          Board-ready chart showing cost/benefit structure with numeric labels
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-[9px] text-[#52525b]" style={{ fontWeight: 500 }}>
                  Baseline → Benefits → Costs → Net Value
                </p>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={activeWaterfallData} margin={{ top: 20, right: 15, left: 15, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: '#52525b', fontWeight: 500 }}
                    axisLine={{ stroke: '#d4d4d8' }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#52525b', fontWeight: 500 }}
                    axisLine={{ stroke: '#d4d4d8' }}
                    label={{ value: '($K)', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#52525b', fontWeight: 600 } }}
                  />
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-[#18365E] text-white p-3 rounded-lg shadow-xl">
                            <p className="text-[11px] font-semibold mb-1">{data.name}</p>
                            <p className="text-[13px] font-bold mb-2">{data.displayValue}</p>
                            <p className="text-[10px] text-slate-300">
                              {data.type === 'benefit' && 'Benefits: Year 1 Annual Value for the selected scenario'}
                              {data.type === 'cost' && 'Costs: Year 1 investment including implementation and licensing'}
                              {data.type === 'net' && 'Net Value: Year 1 Benefits + Costs'}
                              {data.type === 'baseline' && 'Baseline: Starting point before initiative'}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <ReferenceLine x="Benefits" stroke="#d4d4d8" strokeWidth={2} strokeDasharray="4 4" />
                  <ReferenceLine x="Costs" stroke="#d4d4d8" strokeWidth={2} strokeDasharray="4 4" />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {activeWaterfallData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                    <LabelList
                      dataKey="displayValue"
                      position="top"
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        fill: '#27272a'
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* RIGHT: Sensitivity + AI Insight */}
          <div className="space-y-6">
            {/* Sensitivity Analysis */}
            <div className="bg-white border border-[#d4d4d8] rounded-lg p-3.5">
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-xs text-[#18365E]" style={{ fontWeight: 700 }}>
                    Sensitivity Analysis
                  </h3>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3.5 h-3.5 text-[#71717a] hover:text-[#18365E] cursor-pointer" />
                      </TooltipTrigger>
                      <TooltipContent side="left" className="bg-[#18365E] text-white border-0 max-w-sm">
                        <p className="text-[10px]" style={{ fontWeight: 500 }}>
                          Ranking shows which assumptions have greatest impact on Net Value
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              <p className="text-[9px] text-[#52525b] mb-2.5" style={{ fontWeight: 500 }}>
                Impact of ±20% variance (ranked by magnitude)
              </p>

              <div className="space-y-2.5">
                {activeSensitivityData.map((item) => {
                  const isPrimary = item.rank <= 2;
                  const isSecondary = item.rank >= 3 && item.rank <= 5;
                  const isLowPriority = item.rank > 5;

                  return (
                    <TooltipProvider key={item.rank}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className={`${isLowPriority ? 'opacity-85' : 'opacity-100'}`}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                {isPrimary && (
                                  <Badge className="text-[10px] px-2 py-0.5 bg-[#FF6E4C] text-white font-bold shadow-sm">
                                    #{item.rank}
                                  </Badge>
                                )}
                                {isSecondary && (
                                  <Badge className="text-[9px] px-1.5 py-0 bg-[#18365E] text-white font-semibold">
                                    #{item.rank}
                                  </Badge>
                                )}
                                {isLowPriority && (
                                  <Badge className="text-[8px] px-1 py-0 bg-slate-400/40 text-slate-600">
                                    #{item.rank}
                                  </Badge>
                                )}
                                <span className={`text-[10px] text-[#27272a] ${isPrimary ? 'font-bold' : 'font-medium'}`}>
                                  {item.name}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-[9px] text-[#52525b]" style={{ fontWeight: 500 }}>
                                  {item.label}
                                </span>
                                <Edit2 className="w-3 h-3 text-[#71717a] hover:text-[#FF6E4C] cursor-pointer" />
                              </div>
                            </div>
                            <div className={`${isPrimary ? 'h-5 border-2' : 'h-4 border'} bg-[#fafafa] rounded overflow-hidden relative ${isPrimary ? 'border-[#FF6E4C]/30' : 'border-[#e4e4e7]'
                              }`}>
                              <div className="absolute inset-0 flex">
                                <div
                                  className={`${isPrimary ? 'bg-[#dc2626]' : 'bg-[#ef4444]/70'} h-full transition-all`}
                                  style={{ width: `${50 - (Math.abs(item.negative) / (Math.abs(item.negative) + item.positive)) * 50}%` }}
                                />
                                <div className="w-px bg-[#71717a]" />
                                <div
                                  className={`${isPrimary ? 'bg-[#059669]' : 'bg-[#10b981]/70'} h-full transition-all`}
                                  style={{ width: `${(item.positive / (Math.abs(item.negative) + item.positive)) * 50}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="bg-[#18365E] text-white border-0 max-w-sm">
                          <div className="space-y-1.5">
                            <p className="text-[10px] font-semibold">
                              {item.rank === 1 ? 'Strongest impact on Net Value' :
                                item.rank === 2 ? '2nd strongest impact on Net Value' :
                                  `#${item.rank} impact on Net Value`}
                            </p>
                            <p className="text-[9px] text-slate-300">{item.impactExplanation}</p>
                            <p className="text-[9px] text-slate-300 pt-1 border-t border-slate-600">
                              ±${Math.max(Math.abs(item.negative), item.positive)}K impact on Net Value
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>

              <div className="mt-3 pt-3 border-t border-[#e4e4e7]">
                <div className="flex items-center gap-3 text-[9px]">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-1.5 bg-[#ef4444]/80 rounded" />
                    <span className="text-[#52525b]" style={{ fontWeight: 500 }}>Downside</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-1.5 bg-[#10b981]/80 rounded" />
                    <span className="text-[#52525b]" style={{ fontWeight: 500 }}>Upside</span>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Insight Panel */}
            <div className="relative p-[2px] rounded-lg bg-gradient-to-r from-[#3b82f6]/20 via-[#8b5cf6]/20 to-[#3b82f6]/20 bg-[length:200%_200%] animate-[gradient_3s_ease_infinite]">
              <div className="bg-white rounded-lg p-3.5 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10">
                <div className="flex items-start gap-2 mb-2">
                  <div className="relative">
                    <Sparkles className="w-4 h-4 text-[#FF6E4C] animate-pulse" />
                    <div className="absolute inset-0 animate-ping opacity-20">
                      <Sparkles className="w-4 h-4 text-[#FF6E4C]" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <h4 className="text-xs text-[#18365E] cursor-help flex items-center gap-1" style={{ fontWeight: 700 }}>
                            AI Insight
                            <Badge className="text-[8px] px-1 py-0 bg-[#FF6E4C]/20 text-[#FF6E4C] border border-[#FF6E4C]/30">
                              AI-Generated
                            </Badge>
                          </h4>
                        </TooltipTrigger>
                        <TooltipContent className="bg-[#18365E] text-white border-0">
                          <p className="text-[10px]">
                            <Sparkles className="w-3 h-3 inline text-[#FF6E4C] mr-1" />
                            AI-generated insight based on scenario assumptions & sensitivity modeling
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
                <p className="text-[10px] text-[#27272a] mb-2.5" style={{ fontWeight: 500, lineHeight: '1.6' }}>
                  {aiInsightText ??
                    'Adoption rate (#1) and cost reduction (#2) are your highest-impact assumptions. A 10% variance changes NPV by $600K+. These are your critical validation priorities.'}
                </p>
                <button className="text-[10px] text-[#18365E] hover:text-[#FF6E4C] transition-colors flex items-center gap-0.5" style={{ fontWeight: 600 }}>
                  <ArrowUpRight className="w-3 h-3" />
                  View Resource Center
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Executive Narrative Section */}
        <div className="mt-8">
          <div className="flex items-center gap-1.5 mb-3">
            <h3 className="text-[11px] text-[#52525b] uppercase tracking-wider" style={{ fontWeight: 700 }}>
              Executive Narrative (Step 2 Output)
            </h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-3.5 h-3.5 text-[#71717a] hover:text-[#18365E] cursor-pointer" />
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-[#18365E] text-white border-0 max-w-sm">
                  <p className="text-[10px]" style={{ fontWeight: 500 }}>
                    CFO-ready and Board-ready summaries that carry forward to approval
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-[9px] text-[#52525b] mb-3.5" style={{ fontWeight: 500 }}>
            Executive-ready summaries. Edit below or use AI Assist.
          </p>

          <div className="grid grid-cols-2 gap-5">
            {/* Financial Story */}
            <div className="bg-white border border-[#d4d4d8] rounded-lg overflow-hidden">
              <div className="h-0.5 bg-gradient-to-r from-[#FF6E4C] to-[#FF6E4C]/40" />
              <div className="p-3.5">
                <div className="flex items-center justify-between mb-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#10b981]/10 flex items-center justify-center">
                      <DollarSign className="w-4 h-4 text-[#10b981]" />
                    </div>
                    <h4 className="text-xs text-[#18365E]" style={{ fontWeight: 700 }}>
                      Financial Story (CFO-Ready)
                    </h4>
                  </div>
                  <button
                    onClick={() => handleCopy(financialNarrative, 'financial')}
                    className="text-[10px] text-[#71717a] hover:text-[#18365E] transition-colors flex items-center gap-0.5"
                    style={{ fontWeight: 600 }}
                  >
                    <Copy className="w-3.5 h-3.5" />
                    {copiedText === 'financial' ? 'Copied!' : 'Copy'}
                  </button>
                </div>

                <textarea
                  value={financialNarrative}
                  onChange={(e) => setFinancialNarrative(e.target.value)}
                  className="w-full h-32 p-2.5 bg-[#fafafa] border border-[#e4e4e7] rounded text-[10px] text-[#27272a] focus:outline-none focus:ring-1 focus:ring-[#FF6E4C] focus:border-[#FF6E4C] resize-none"
                  style={{ fontWeight: 500, lineHeight: '1.7', letterSpacing: '0.01em' }}
                />

                <div className="mt-2.5 pt-2.5 border-t border-[#e4e4e7] flex items-center gap-2">
                  <button
                    onClick={onExportOnePager || (() => console.log('Export One-Pager'))}
                    className="flex-1 px-3 py-2 bg-[#18365E] text-white rounded text-[10px] flex items-center justify-center gap-1 hover:bg-[#18365E]/90 shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
                    style={{ fontWeight: 600 }}
                  >
                    <FileDown className="w-3.5 h-3.5" />
                    Export One-Pager
                  </button>
                  <button className="px-3 py-2 bg-white text-[#18365E] border border-[#d4d4d8] rounded text-[10px] flex items-center gap-1 hover:bg-[#fafafa] shadow-sm hover:shadow-md transition-all duration-200" style={{ fontWeight: 600 }}>
                    <Sparkles className="w-3.5 h-3.5" />
                    AI Assist
                  </button>
                </div>
              </div>
            </div>

            {/* Strategic Story */}
            <div className="bg-white border border-[#d4d4d8] rounded-lg overflow-hidden">
              <div className="h-0.5 bg-gradient-to-r from-[#FF6E4C] to-[#FF6E4C]/40" />
              <div className="p-3.5">
                <div className="flex items-center justify-between mb-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#3b82f6]/10 flex items-center justify-center">
                      <Compass className="w-4 h-4 text-[#3b82f6]" />
                    </div>
                    <h4 className="text-xs text-[#18365E]" style={{ fontWeight: 700 }}>
                      Strategic Story (Board-Ready)
                    </h4>
                  </div>
                  <button
                    onClick={() => handleCopy(strategicNarrative, 'strategic')}
                    className="text-[10px] text-[#71717a] hover:text-[#18365E] transition-colors flex items-center gap-0.5"
                    style={{ fontWeight: 600 }}
                  >
                    <Copy className="w-3.5 h-3.5" />
                    {copiedText === 'strategic' ? 'Copied!' : 'Copy'}
                  </button>
                </div>

                <textarea
                  value={strategicNarrative}
                  onChange={(e) => setStrategicNarrative(e.target.value)}
                  className="w-full h-32 p-2.5 bg-[#fafafa] border border-[#e4e4e7] rounded text-[10px] text-[#27272a] focus:outline-none focus:ring-1 focus:ring-[#FF6E4C] focus:border-[#FF6E4C] resize-none"
                  style={{ fontWeight: 500, lineHeight: '1.7', letterSpacing: '0.01em' }}
                />

                <div className="mt-2.5 pt-2.5 border-t border-[#e4e4e7] flex items-center gap-2">
                  <button
                    onClick={onExportBoardDeck || (() => console.log('Export Board Deck'))}
                    className="flex-1 px-3 py-2 bg-[#FF6E4C] text-white rounded text-[10px] flex items-center justify-center gap-1 hover:bg-[#FF6E4C]/90 shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
                    style={{ fontWeight: 600 }}
                  >
                    <FileDown className="w-3.5 h-3.5" />
                    Export for Board Deck
                  </button>
                  <button className="px-3 py-2 bg-white text-[#18365E] border border-[#d4d4d8] rounded text-[10px] flex items-center gap-1 hover:bg-[#fafafa] shadow-sm hover:shadow-md transition-all duration-200" style={{ fontWeight: 600 }}>
                    <Sparkles className="w-3.5 h-3.5" />
                    AI Assist
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gradient animation */}
      <style jsx global>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </div>
  );
}

